import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchAllOrgScopedRolesInGroup,
  fetchRostersForGroups,
  removeUserFromGroup,
  unassignRole,
} from '../../actions/roleActions';
import { useDispatch, useSelector } from 'react-redux';
import isUserOnlyAdminInGroup from '../Roles/RolesUtils/isUserOnlyAdminInGroup';
import { useNavigate } from 'react-router';
import { StylishConfirmDialog } from '../DesignSystems/New/StylishConfirmDialog';
import NewRoleAssignmentDialog from '../Roles/NewRoleAssignmentDialog';
import LegacyPratusTable from '../DesignSystems/PratusTable/LegacyPratusTable';
import { AddMemberDialog } from './AddMemberDialog';
import BulkUserImportDialog from './BulkUserImportDialog';
import { useSubscriptionLevel } from '../SubscriptionManagement/useSubscriptionLevel';
import LockIcon from '../DesignSystems/LockIcon';
import { changeGroupAdminNameToOrgAdmin } from '../Roles/RolesUtils/groupToOrgTransformations';
import useIsOrgAdmin from '../../hooks/useIsOrgAdmin';
import { ADMIN_ROLES } from 'constants/group';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useAors } from 'hooks/useAors';
import { StylishNewSearchBar } from '../DesignSystems/New/StylishNewSearchBar';

// TODO: pagination.
const OrgMembers: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { freemium } = useSubscriptionLevel();

  const [stagedRoleRemoval, setStagedRoleRemoval] = useState<
    [RosterItem, RoleAssignment] | null
  >(null);
  const [stagedUserRemoval, setStagedUserRemoval] = useState<RosterItem | null>(
    null
  );
  const [addNewRoleUser, setAddNewRoleUser] = useState<string | null>(null);
  const [showAddMemberDialog, setShowAddMemberDialog] =
    useState<boolean>(false);
  const [showBulkImportDialog, setShowBulkImportDialog] =
    useState<boolean>(false);
  const [editRow, setEditRow] = useState<string | null>(null);
  const [memberSearchTerm, setMemberSearchTerm] = useState<string>('');

  const selectedGroup = useSelector((state: any) => {
    return state.app.currentlySelectedGroup as Group;
  });
  const { user_guid } = useSelector((state: any) => {
    return state.app.user as User;
  });
  const rosters = useSelector((state: any) => {
    return (state.app.rostersForGroups as RosterItem[]) || [];
  });
  const reduxValidateRBACPermissionForActionResult = useSelector(
    (state: any) => {
      return state.app.validateRBACPermissionForActionResult;
    }
  );

  const isOrgAdmin = useIsOrgAdmin();
  const { data: aors = [] } = useAors();

  const roster = useMemo<RosterItem[]>(() => {
    if (rosters.length === 0) return [];
    return rosters.filter(
      (member) => member.group_guid === selectedGroup.group_guid
    );
  }, [rosters, selectedGroup.group_guid]);

  const filteredRoster = useMemo<RosterItem[]>(() => {
    const normalizedSearch = memberSearchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return roster;
    }

    return roster.filter((member) => {
      const memberName = (member.user_name || '').toLowerCase();
      const memberEmail = (member.email_address || '').toLowerCase();

      return (
        memberName.includes(normalizedSearch) ||
        memberEmail.includes(normalizedSearch)
      );
    });
  }, [roster, memberSearchTerm]);

  useEffect(() => {
    if (typeof user_guid !== 'undefined' && selectedGroup) {
      dispatch(fetchRostersForGroups([selectedGroup.group_guid]));
      dispatch(fetchAllOrgScopedRolesInGroup(selectedGroup.group_guid));
    }
  }, [dispatch, user_guid, selectedGroup]);

  const removeUserConfirmed = async () => {
    if (!stagedUserRemoval) {
      return;
    }
    await dispatch(
      removeUserFromGroup(stagedUserRemoval.user_guid, selectedGroup.group_guid)
    );
    setStagedUserRemoval(null);
  };

  const removeRoleConfirmed = async () => {
    if (!stagedRoleRemoval) {
      return;
    }
    const [rosterItem, roleAssignment] = stagedRoleRemoval;
    await dispatch(
      unassignRole({
        ...roleAssignment,
        user_guid: rosterItem.user_guid,
      })
    );
    setStagedRoleRemoval(null);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3 flex-grow-1">
          <span className="fw-bold mb-0">
            {filteredRoster.length}{' '}
            {filteredRoster.length === 1 ? 'Member' : 'Members'}
          </span>
          <div className="col-3">
            <StylishNewSearchBar
              value={memberSearchTerm}
              onChangeSearchTerm={setMemberSearchTerm}
              placeholder="Search members by name or email"
            />
          </div>
        </div>
        <button
          className="button button--primary"
          onClick={() => setShowAddMemberDialog(true)}
          disabled={freemium}
        >
          <span>Invite New Members</span>
          {freemium ? <LockIcon /> : null}
        </button>
      </div>
      <div className="form-block">
        <LegacyPratusTable
          keyField={'user_guid'}
          columns={[
            {
              dataField: 'email_address',
              text: 'Member Email',
              sort: true,
              formatter: (cell: string, row: RosterItem) => {
                return (
                  <div className="d-flex align-items-center">
                    <div className="avatar-initials sml">
                      {row.user_name
                        ?.split(' ')
                        .map((word) => word[0])
                        .join('')
                        .slice(0, 3)
                        .toUpperCase()}
                    </div>
                    <div className="d-flex flex-column ms-3 justify-content-center overflow-auto">
                      {row.user_name ? (
                        <>
                          <div>{row.user_name}</div>
                          <div className="text-muted pt-2">
                            {row.email_address}
                          </div>
                        </>
                      ) : (
                        <div>{row.email_address}</div>
                      )}
                    </div>
                  </div>
                );
              },
            },
            {
              dataField: 'role_assignments',
              formatter: (cell: RoleAssignment[]) => {
                return cell
                  .map((ra) => {
                    const roleName = changeGroupAdminNameToOrgAdmin(ra.name);
                    const scopeName =
                      ra.scope_type === 'aor' && ra.scope_item_name
                        ? ra.scope_item_name
                        : selectedGroup.group_name;
                    return `${scopeName}: ${roleName}`;
                  })
                  .join(', ');
              },
              text: 'Organization Roles',
              sort: true,
            },
            {
              dataField: '',
              text: '',
              isDummyField: true,
              headerStyle: { width: '54px' },
              formatter: (cell: any, rosterItem) => {
                // TODO: Apply when permissions ready for front-end
                // return isUserAllowedToBeRemovedFromGroup(rosterItem, user_guid, reduxValidateRBACPermissionForActionResult, selectedGroup, rosters) ? (...) : null
                return (
                  <div className="d-flex">
                    <button
                      className="button button--icon border-0 fs-6"
                      onClick={() => {
                        setStagedUserRemoval(rosterItem);
                      }}
                    >
                      <i className="material-symbols-outlined tw-hover:text-error">
                        delete
                      </i>
                    </button>
                  </div>
                );
              },
            },
          ]}
          rows={filteredRoster}
          expandRow={{
            parentClassName: 'parent-expand-tr',
            className: 'child-expand-tr',
            renderer: (row: RosterItem) => {
              const allowProfileEdit =
                row.user_guid === user_guid &&
                row.role_assignments.some((ra) =>
                  ADMIN_ROLES.includes(ra.name)
                ) &&
                row.allow_admin_profile_edits;
              return (
                <div className="row">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center justify-content-between border-bottom tw-border-b-neutral-700 tw-mb-3">
                      <strong className="py-1">Contact</strong>
                      {allowProfileEdit ? (
                        <button
                          className="button button--link"
                          onClick={() => navigate(`/profile/${row.user_guid}`)}
                        >
                          Edit
                        </button>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <label className="form-label d-block mb-0">Email</label>
                      <span className="tw-ps-3 fst-italic">
                        {row.email_address}
                      </span>
                    </div>
                    <div className="mb-3">
                      <label className="form-label d-block mb-0">Mobile</label>
                      <span className="tw-ps-3 fst-italic">
                        {row?.profile_settings?.mobile_phone_number}
                      </span>
                    </div>
                    <div className="mb-3">
                      <label className="form-label d-block mb-0">Home</label>
                      <span className="tw-ps-3 fst-italic">
                        {row?.profile_settings?.home_phone_number}
                      </span>
                    </div>
                    <div className="mb-3">
                      <label className="form-label d-block mb-0">Pager</label>
                      <span className="tw-ps-3 fst-italic">
                        {row?.profile_settings?.pager_address}
                      </span>
                    </div>
                    <div className="mb-3">
                      <label className="form-label d-block mb-0">
                        Home AOR
                      </label>
                      <span className="tw-ps-3 fst-italic">
                        {row.home_aor
                          ? aors.find(
                              (aor) =>
                                aor.id === row.home_aor ||
                                aor.item_id === row.home_aor
                            )?.name || row.home_aor
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center justify-content-between border-bottom tw-border-b-neutral-700 tw-mb-3">
                      <strong className="py-1">Positions and Roles</strong>
                      {reduxValidateRBACPermissionForActionResult[
                        'Assign Member to Role'
                      ] ? (
                        row.external_user ? (
                          <SharedIcon
                            iconName="info"
                            size="sm"
                            title={
                              'You cannot edit the roles of an external user'
                            }
                            aria-label={
                              'You cannot edit the roles of an external user'
                            }
                            style={{ cursor: 'default' }}
                          />
                        ) : (
                          <button
                            className="button button--link"
                            onClick={() => {
                              setEditRow((edit) =>
                                edit === row.user_guid ? null : row.user_guid
                              );
                            }}
                          >
                            Edit
                          </button>
                        )
                      ) : null}
                    </div>
                    <div className="d-flex pe-3 flex-wrap align-items-baseline">
                      {row.role_assignments?.map((ra) => {
                        const canRemoveRole =
                          editRow === row.user_guid &&
                          reduxValidateRBACPermissionForActionResult[
                            'Unassign Member from Role'
                          ] &&
                          (!isUserOnlyAdminInGroup(
                            selectedGroup.group_guid,
                            rosters
                          ) ||
                            !ADMIN_ROLES.includes(ra.name));
                        return (
                          <span
                            className="badge me-3 mb-3"
                            key={'GroupMembers-' + ra.id}
                          >
                            <strong>
                              {changeGroupAdminNameToOrgAdmin(ra.name)}
                            </strong>
                            :{' '}
                            {ra.scope_type === 'aor' && ra.scope_item_name
                              ? ra.scope_item_name
                              : selectedGroup.group_name}
                            {canRemoveRole ? (
                              <button
                                type="button"
                                onClick={() => setStagedRoleRemoval([row, ra])}
                              >
                                <i className="material-symbols-outlined">
                                  close
                                </i>
                              </button>
                            ) : null}
                          </span>
                        );
                      })}
                      {editRow === row.user_guid &&
                      !!reduxValidateRBACPermissionForActionResult[
                        'Assign Member to Role'
                      ] ? (
                        <button
                          className="button button--link"
                          onClick={() => setAddNewRoleUser(row.user_guid)}
                        >
                          Add User Role
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="button-group mt-4">
                    {row.allow_admin_profile_edits && isOrgAdmin && (
                      <button
                        onClick={() => navigate(`/profile/${row.user_guid}`)}
                        className="button button--link"
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>
                </div>
              );
            },
            showExpandColumn: true,
          }}
        />
      </div>
      {stagedUserRemoval !== null ? (
        <StylishConfirmDialog
          show={true}
          dialogTitle="Delete Confirmation"
          dialogContent={`Remove ${stagedUserRemoval.email_address} from this group?`}
          onClose={() => setStagedUserRemoval(null)}
          onConfirm={() => removeUserConfirmed()}
        />
      ) : null}
      {stagedRoleRemoval !== null ? (
        <StylishConfirmDialog
          show={true}
          dialogTitle="Delete Confirmation"
          dialogContent={`Remove ${stagedRoleRemoval[0].email_address} from the role ${stagedRoleRemoval[1].name}?`}
          onClose={() => setStagedRoleRemoval(null)}
          onConfirm={() => removeRoleConfirmed()}
        />
      ) : null}
      {addNewRoleUser !== null ? (
        <NewRoleAssignmentDialog
          show={true}
          onClose={() => setAddNewRoleUser(null)}
          group_guid={selectedGroup.group_guid}
          assignee_guid={addNewRoleUser}
        />
      ) : null}
      {showAddMemberDialog ? (
        <AddMemberDialog
          show={true}
          onClose={() => setShowAddMemberDialog(false)}
          onBulkImportClick={() => {
            setShowAddMemberDialog(false);
            setShowBulkImportDialog(true);
          }}
        />
      ) : null}
      {showBulkImportDialog ? (
        <BulkUserImportDialog
          show={true}
          onClose={() => setShowBulkImportDialog(false)}
        />
      ) : null}
    </>
  );
};
export default OrgMembers;
